import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import driverRouteService from '../../services/driverRouteService';
import routeService, { isRouteInProgress, isRoutePending } from '../../services/routeService';
import { getFriendlyErrorMessage } from '../../utils/errorHandler';
import { ROUTE_POINT_ACTION_OPTIONS } from '../Rotas/routePointAction';
import RoutePointStateBadge from '../Rotas/RoutePointStateBadge';
import { ROUTES } from '../../routes';
import ConfirmModal from '../shared/ConfirmModal';
import CopyAddressButton from './CopyAddressButton';
import { useDriverRoute } from './DriverRouteContext';
import OpenNavigationButton from './OpenNavigationButton';
import {
    getAdjacentPontoId,
    getFirstPontoId,
    getPontoById,
    hasPontoPendente,
    isPontoInicioMarker,
    isPontoPendente,
} from './roteiroNavigation';
import {
    AddressCard,
    AddressText,
    BackLink,
    FieldBlock,
    FieldLabel,
    FieldValue,
    ItemsTable,
    NavArrowButton,
    PageTitle,
    PageWrap,
    PontoHeader,
    PontoHeaderCenter,
    PontoPanel,
    PontoSwipeArea,
    PontoSwipeContent,
    PontoTitle,
    PrimaryActionButton,
    UpdatedNotice,
} from './Roteiros.styles';
import { usePontoSlideTransition } from './usePontoSlideTransition';

function ChevronLeft() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    );
}

function ChevronRight() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    );
}

function getActionLabel(action) {
    const option = ROUTE_POINT_ACTION_OPTIONS.find((o) => o.value === action);
    return option?.label || action || '—';
}

function PontoContent({ ponto, roteiro, canUpdateStatus, showUpdatedNotice, navigate }) {
    return (
        <>
            <FieldBlock>
                <FieldLabel>Ação</FieldLabel>
                <FieldValue>{getActionLabel(ponto.action)}</FieldValue>
            </FieldBlock>

            <FieldBlock>
                <FieldLabel>Endereço completo</FieldLabel>
                <AddressCard>
                    <AddressText>{ponto.fullAddress || '—'}</AddressText>
                    {ponto.fullAddress && ponto.fullAddress !== '—' ? (
                        <>
                            <CopyAddressButton address={ponto.fullAddress} />
                            <OpenNavigationButton address={ponto.fullAddress} />
                        </>
                    ) : null}
                </AddressCard>
            </FieldBlock>

            <FieldBlock>
                <FieldLabel>Instruções de entrega</FieldLabel>
                <FieldValue>{ponto.deliveryInstructions || '—'}</FieldValue>
            </FieldBlock>

            <FieldBlock>
                <FieldLabel>Observação do ponto</FieldLabel>
                <FieldValue>{ponto.observation || '—'}</FieldValue>
            </FieldBlock>

            <FieldBlock>
                <FieldLabel>Itens da entrega</FieldLabel>
                {ponto.items?.length ? (
                    <ItemsTable>
                        <thead>
                            <tr>
                                <th>qtd</th>
                                <th>descrição</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ponto.items.map((item, index) => (
                                <tr key={`${item.description}-${index}`}>
                                    <td>{item.qty}</td>
                                    <td>{item.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </ItemsTable>
                ) : (
                    <FieldValue>—</FieldValue>
                )}
            </FieldBlock>

            {canUpdateStatus ? (
                <PrimaryActionButton
                    onClick={() => navigate(ROUTES.ROTEIROS_PONTO_STATUS(roteiro.id, ponto.id))}
                >
                    Atualizar status
                </PrimaryActionButton>
            ) : showUpdatedNotice ? (
                <UpdatedNotice>Status deste ponto já foi registrado.</UpdatedNotice>
            ) : null}
        </>
    );
}

export default function PontoDetailPage() {
    const navigate = useNavigate();
    const { pontoId } = useParams();
    const { roteiro, loading, error, refreshRoute, reloadRoute } = useDriverRoute();

    const [starting, setStarting] = useState(false);
    const [startError, setStartError] = useState(null);
    const [finishing, setFinishing] = useState(false);
    const [finishError, setFinishError] = useState(null);
    const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);

    const resolvedPonto = useMemo(() => {
        if (!roteiro) return null;
        if (isPontoInicioMarker(pontoId)) return null;
        return getPontoById(roteiro, pontoId);
    }, [roteiro, pontoId]);

    useEffect(() => {
        if (!roteiro || loading) return;

        const firstPontoId = getFirstPontoId(roteiro);
        if (!firstPontoId) return;

        if (isPontoInicioMarker(pontoId) || !resolvedPonto) {
            navigate(ROUTES.ROTEIROS_PONTO(roteiro.id, firstPontoId), { replace: true });
        }
    }, [roteiro, loading, pontoId, resolvedPonto, navigate]);

    const ponto = resolvedPonto;
    const prevPontoId = roteiro && ponto ? getAdjacentPontoId(roteiro, ponto.id, 'prev') : null;
    const nextPontoId = roteiro && ponto ? getAdjacentPontoId(roteiro, ponto.id, 'next') : null;
    const isPontoPending = ponto ? isPontoPendente(ponto.state) : false;
    const canUpdateStatus = roteiro ? isRouteInProgress(roteiro.state) && isPontoPending : false;
    const showUpdatedNotice = !isPontoPending;
    const showStartButton = roteiro
        ? isRoutePending(roteiro.state) && hasPontoPendente(roteiro)
        : false;
    const showFinishButton = roteiro
        ? isRouteInProgress(roteiro.state)
            && (roteiro.points?.length > 0)
            && !hasPontoPendente(roteiro)
        : false;

    const handleStartRoute = useCallback(async () => {
        if (!roteiro?.id) return;

        setStartError(null);
        setStarting(true);

        try {
            await driverRouteService.start(roteiro.id);
            await refreshRoute();
        } catch (err) {
            setStartError(err?.message || 'Não foi possível iniciar o roteiro.');
        } finally {
            setStarting(false);
        }
    }, [refreshRoute, roteiro?.id]);

    const handleFinishRoute = useCallback(async () => {
        if (!roteiro?.id) return;

        setFinishConfirmOpen(false);
        setFinishError(null);
        setFinishing(true);

        try {
            await routeService.finish(roteiro.id);
            await refreshRoute();
        } catch (err) {
            setFinishError(
                getFriendlyErrorMessage(err) || 'Não foi possível finalizar o roteiro.',
            );
        } finally {
            setFinishing(false);
        }
    }, [refreshRoute, roteiro?.id]);

    const navigateToPonto = useCallback((targetPontoId) => {
        if (!targetPontoId || !roteiro) return;
        navigate(ROUTES.ROTEIROS_PONTO(roteiro.id, targetPontoId));
    }, [navigate, roteiro]);

    const {
        dragOffset,
        isDragging,
        exitDirection,
        enterDirection,
        isTransitioning,
        goNext,
        goPrev,
        bindSwipe,
    } = usePontoSlideTransition({
        enabled: Boolean(roteiro && ponto),
        pontoId,
        onNavigateNext: nextPontoId != null ? () => navigateToPonto(nextPontoId) : null,
        onNavigatePrev: prevPontoId != null ? () => navigateToPonto(prevPontoId) : null,
    });

    if (loading) {
        return (
            <PageWrap>
                <BackLink type="button" onClick={() => navigate(ROUTES.ROTEIROS)}>
                    ← Voltar aos roteiros
                </BackLink>
                <PageTitle>Roteiros</PageTitle>
                <FieldValue>Carregando roteiro…</FieldValue>
            </PageWrap>
        );
    }

    if (error) {
        return (
            <PageWrap>
                <BackLink type="button" onClick={() => navigate(ROUTES.ROTEIROS)}>
                    ← Voltar aos roteiros
                </BackLink>
                <PageTitle>Roteiros</PageTitle>
                <FieldValue style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</FieldValue>
                <PrimaryActionButton type="button" onClick={reloadRoute}>
                    Tentar novamente
                </PrimaryActionButton>
            </PageWrap>
        );
    }

    if (!roteiro || !ponto) {
        if (roteiro && !getFirstPontoId(roteiro)) {
            return (
                <PageWrap>
                    <BackLink type="button" onClick={() => navigate(ROUTES.ROTEIROS)}>
                        ← Voltar aos roteiros
                    </BackLink>
                    <PageTitle>Roteiros</PageTitle>
                    <FieldValue>Este roteiro não possui pontos.</FieldValue>
                </PageWrap>
            );
        }

        return null;
    }

    return (
        <PageWrap>
            <BackLink type="button" onClick={() => navigate(ROUTES.ROTEIROS)}>
                ← Voltar aos roteiros
            </BackLink>
            <PageTitle>Roteiros</PageTitle>

            {showStartButton ? (
                <div style={{ marginBottom: 16 }}>
                    <PrimaryActionButton
                        type="button"
                        disabled={starting}
                        onClick={handleStartRoute}
                    >
                        {starting ? 'Iniciando…' : 'Iniciar roteiro'}
                    </PrimaryActionButton>
                    {startError ? (
                        <FieldValue style={{ color: '#b91c1c', marginTop: 8 }}>{startError}</FieldValue>
                    ) : null}
                </div>
            ) : null}

            {showFinishButton ? (
                <div style={{ marginBottom: 16 }}>
                    <PrimaryActionButton
                        type="button"
                        disabled={finishing}
                        onClick={() => setFinishConfirmOpen(true)}
                    >
                        {finishing ? 'Finalizando…' : 'Finalizar roteiro'}
                    </PrimaryActionButton>
                    {finishError ? (
                        <FieldValue style={{ color: '#b91c1c', marginTop: 8 }}>{finishError}</FieldValue>
                    ) : null}
                </div>
            ) : null}

            <ConfirmModal
                open={finishConfirmOpen}
                title="Finalizar roteiro"
                message="Deseja finalizar este roteiro? Só confirme se todos os pontos já foram concluídos."
                confirmLabel="Finalizar roteiro"
                cancelLabel="Cancelar"
                confirmVariant="primary"
                onConfirm={handleFinishRoute}
                onCancel={() => !finishing && setFinishConfirmOpen(false)}
            />

            <PontoPanel>
                <PontoHeader>
                    <NavArrowButton
                        type="button"
                        aria-label="Ponto anterior"
                        disabled={!prevPontoId || isTransitioning}
                        onClick={goPrev}
                    >
                        <ChevronLeft />
                    </NavArrowButton>

                    <PontoHeaderCenter
                        key={ponto.id}
                        $exitDirection={exitDirection}
                        $enterDirection={enterDirection}
                    >
                        <PontoTitle>Ponto {ponto.index}</PontoTitle>
                        <RoutePointStateBadge state={ponto.state} />
                    </PontoHeaderCenter>

                    <NavArrowButton
                        type="button"
                        aria-label="Próximo ponto"
                        disabled={!nextPontoId || isTransitioning}
                        onClick={goNext}
                    >
                        <ChevronRight />
                    </NavArrowButton>
                </PontoHeader>

                <PontoSwipeArea {...bindSwipe()}>
                    <PontoSwipeContent
                        key={ponto.id}
                        $offset={isDragging ? dragOffset : 0}
                        $dragging={isDragging}
                        $exitDirection={exitDirection}
                        $enterDirection={enterDirection}
                    >
                        <PontoContent
                            ponto={ponto}
                            roteiro={roteiro}
                            canUpdateStatus={canUpdateStatus}
                            showUpdatedNotice={showUpdatedNotice}
                            navigate={navigate}
                        />
                    </PontoSwipeContent>
                </PontoSwipeArea>
            </PontoPanel>
        </PageWrap>
    );
}
